
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line

/**
 * IPv4Range struct
 */
class IPv4Range {
  /**
   * Initialize struct
   * @param {!Array=} fromInclusive
   * @param {!Array=} toInclusive
   * @constructor
   */
  constructor (fromInclusive = [], toInclusive = []) {
    this.FromInclusive = fromInclusive
    this.ToInclusive = toInclusive
  }

  /**
   * Copy struct (shallow copy)
   * @this {!IPv4Range}
   * @param {!IPv4Range} other Other struct
   * @returns {!IPv4Range} This struct
   */
  copy (other) {
    if (other.FromInclusive != null) {
      this.FromInclusive = []
      for (let item of other.FromInclusive) {
        if (item != null) {
          let tempItem
          tempItem = item
          this.FromInclusive.push(tempItem)
        } else {
          this.FromInclusive.push(null)
        }
      }
    } else {
      this.FromInclusive = null
    }
    if (other.ToInclusive != null) {
      this.ToInclusive = []
      for (let item of other.ToInclusive) {
        if (item != null) {
          let tempItem
          tempItem = item
          this.ToInclusive.push(tempItem)
        } else {
          this.ToInclusive.push(null)
        }
      }
    } else {
      this.ToInclusive = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!IPv4Range}
   * @returns {!IPv4Range} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new IPv4RangeModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new IPv4RangeModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!IPv4Range}
   * @param {!IPv4Range} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof IPv4Range)) {
      throw new TypeError('Instance of IPv4Range is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!IPv4Range}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      FromInclusive: ((this.FromInclusive != null) ? Array.from(this.FromInclusive, item => ((item != null) ? item : null)) : null),
      ToInclusive: ((this.ToInclusive != null) ? Array.from(this.ToInclusive, item => ((item != null) ? item : null)) : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return IPv4Range.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!IPv4Range} other Object value
   * @returns {!IPv4Range} Created struct
   */
  static fromObject (other) {
    return new IPv4Range().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!IPv4Range}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return IPv4Range.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!IPv4Range}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 209
  }
}

export { IPv4Range };

/**
 * Fast Binary Encoding IPv4Range field model
 */
class FieldModelIPv4Range extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.FromInclusive = new fbe.FieldModelArray(new fbe.FieldModelByte(buffer, 4 + 4), buffer, 4 + 4, 4)
    this.ToInclusive = new fbe.FieldModelArray(new fbe.FieldModelByte(buffer, this.FromInclusive.fbeOffset + this.FromInclusive.fbeSize), buffer, this.FromInclusive.fbeOffset + this.FromInclusive.fbeSize, 4)
  }

  /**
   * Get the field size
   * @this {!FieldModelIPv4Range}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelIPv4Range}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.FromInclusive.fbeSize + this.ToInclusive.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelIPv4Range}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.FromInclusive.fbeExtra + this.ToInclusive.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelIPv4Range}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelIPv4Range.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelIPv4Range}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 209
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelIPv4Range}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelIPv4Range}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.FromInclusive.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.FromInclusive.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.FromInclusive.fbeSize

    if ((fbeCurrentSize + this.ToInclusive.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.ToInclusive.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.ToInclusive.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelIPv4Range}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelIPv4Range}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelIPv4Range}
   * @param {!IPv4Range} fbeValue Default value, defaults is new IPv4Range()
   * @returns {!IPv4Range} IPv4Range value
   */
  get (fbeValue = new IPv4Range()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelIPv4Range}
   * @param {!IPv4Range} fbeValue IPv4Range value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.FromInclusive.fbeSize) <= fbeStructSize) {
      this.FromInclusive.get(fbeValue.FromInclusive)
    } else {
      fbeValue.FromInclusive.length = 0
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.FromInclusive.fbeSize

    if ((fbeCurrentSize + this.ToInclusive.fbeSize) <= fbeStructSize) {
      this.ToInclusive.get(fbeValue.ToInclusive)
    } else {
      fbeValue.ToInclusive.length = 0
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.ToInclusive.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelIPv4Range}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelIPv4Range}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelIPv4Range}
   * @param {!IPv4Range} fbeValue IPv4Range value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelIPv4Range}
   * @param {IPv4Range} fbeValue IPv4Range value
   */
  setFields (fbeValue) {
    this.FromInclusive.set(fbeValue.FromInclusive)
    this.ToInclusive.set(fbeValue.ToInclusive)
  }
}

export { FieldModelIPv4Range };

/**
 * Fast Binary Encoding IPv4Range model
 */
class IPv4RangeModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelIPv4Range(this.buffer, 4)
  }

  /**
   * Get the IPv4Range model
   * @this {!IPv4RangeModel}
   * @returns {!FieldModelIPv4Range} model IPv4Range model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!IPv4RangeModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!IPv4RangeModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return IPv4RangeModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!IPv4RangeModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelIPv4Range.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!IPv4RangeModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!IPv4RangeModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!IPv4RangeModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!IPv4RangeModel}
   * @param {!IPv4Range} value IPv4Range value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!IPv4RangeModel}
   * @param {!IPv4Range} value IPv4Range value, defaults is new IPv4Range()
   * @return {!object} Deserialized IPv4Range value and its size
   */
  deserialize (value = new IPv4Range()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new IPv4Range(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new IPv4Range(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!IPv4RangeModel}
   * @param {!number} prev Previous IPv4Range model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { IPv4RangeModel };
